var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-wiki-editor.ts
import { subscribeByID } from "@adornis/baseql/operations/mongo.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { WindowSizeController } from "@adornis/chemistry/controllers/window-size-controller.js";
import { acss } from "@adornis/chemistry/directives/acss.js";
import { css } from "@adornis/chemistry/directives/css.js";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { baseConfig } from "@adornis/config/baseConfig.js";
import { XDialog } from "@adornis/dialog/x-dialog.js";
import "@adornis/entity-picker/client/x-entity-picker.js";
import { AdornisFilter } from "@adornis/filter/AdornisFilter.js";
import { StringAspect } from "@adornis/filter/aspect-schemas/StringAspect.js";
import { fullEditorConfig } from "@adornis/html-based-buildify/client/editor/presets/full-editor.js";
import "@adornis/html-based-buildify/client/editor/x-editor.js";
import {
  obsidianLinkPlugin
} from "@adornis/html-based-buildify/client/plugins/ObsidianLinkPlugin/ObsidianLinkPlugin.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { from, of, switchMap, tap } from "rxjs";
import { WikiAPI } from "../api/WikiAPI.js";
import { wikiApplyDraft } from "../api/WikiDraftAPI.js";
import { WikiPermissionAPI } from "../api/WikiPermissionAPI.js";
import { WikiEntry, WikiEntryTemplate } from "../db/WikiEntry.js";
import { WikiEntryDraft, WikiEntryDraftStatus } from "../db/WikiEntryDraft.js";
import { embedding } from "./buildify/embedding-schema.js";
import { preview } from "./buildify/preview-schema.js";
import "./x-wiki-draft-list.js";
import "./x-wiki-splitter-view.js";
var XWikiEditor = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.additionalConfigs = [];
    this._size = new WindowSizeController(this);
    this.t = new TranslationController(this);
    this.entryID = new RXController(this, "");
    this.debug = false;
    this._entry = new RXController(
      this,
      this.entryID.observable.pipe(
        switchMap((id) => {
          if (!id || id === "") {
            return from(WikiAPI.getDefaultTemplateContent()()).pipe(
              switchMap((content) => {
                return of(
                  new WikiEntry({
                    content
                  })
                );
              })
            );
          }
          return subscribeByID(
            WikiEntry,
            id
          )({
            _id: 1,
            title: 1,
            content: 1,
            needEmbeddingSync: 1,
            parentID: 1,
            referenceID: 1,
            status: 1,
            published: 1,
            readPermission: 1,
            writePermission: 1,
            createdAt: 1,
            modifiedAt: 1
          });
        }),
        tap((entry) => {
          if (this.entryID.value && entry?._id !== this.entryID.value) {
            console.error(`entryID and entry._id do not match. entryID: ${this.entryID.value}, entry._id: ${entry?._id}`);
          }
        })
      )
    );
    this._loading = new RXController(this, false);
    this._preview = new RXController(this, false);
    this._permissions = new RXController(
      this,
      WikiPermissionAPI.getPermissions("")({
        name: 1,
        id: 1
      })
    );
  }
  /**
   * Checks if the current entry is a draft
   * @returns {boolean} True if the entry is a WikiEntryDraft
   */
  _isDraft() {
    return this._entry.value instanceof WikiEntryDraft;
  }
  /**
   * Checks if the current entry is a template
   * @returns {boolean} True if the entry is a WikiEntryTemplate
   */
  _isTemplate() {
    return this._entry.value instanceof WikiEntryTemplate;
  }
  _isSmall() {
    return (this._size.width ?? 1400) < 1400;
  }
  /**
   * Creates a draft from the current entry
   * Dispatches 'entry-created' event with the new draft ID
   */
  async _createDraft() {
    if (!this._entry.value) {
      return;
    }
    this._loading.next(true);
    const id = await WikiEntryDraft.ConvertToDraft(this._entry.value).create();
    this.dispatchEvent(
      new CustomEvent("entry-created", {
        detail: {
          value: id
        }
      })
    );
    this._loading.next(false);
  }
  /**
   * Applies the current draft to its parent entry
   * Shows a confirmation dialog before applying
   * Dispatches 'entry-navigate' event to the parent entry after applying
   */
  async _applyDraft() {
    XDialog.guard(
      async () => {
        if (!this._entry.value || !this._isDraft()) {
          return;
        }
        this._loading.next(true);
        await this._entry.value.save();
        const parentID = await wikiApplyDraft(this._entry.value._id)();
        this._loading.next(false);
        this.dispatchEvent(
          new CustomEvent("entry-navigate", {
            detail: {
              value: parentID
            }
          })
        );
      },
      {
        hasToConfirm: true,
        confirmText: this.t.translate("wiki", "actions", "confirmApplyDraft")
      }
    );
  }
  /**
   * Renders the title, parent selection, and edit status section
   * @returns {Renderable} Title and parent edit form
   */
  _titleParentEdit() {
    const columns = new Array(3).fill(this._isSmall() ? "1fr" : "200px").join(" ");
    return html`
      <x-grid
        columns=${columns}
        space="sm"
        ${css({
      alignItems: "center",
      ...this._isSmall() ? {
        paddingTop: this.spacing.sm,
        width: "100%",
        borderTop: "1px solid rgba(0,0,0,0.1)"
      } : {}
    })}
      >
        <x-input
          placeholder=${this.t.translate("field", "wiki", "title")}
          .value=${this._entry.value?.title}
          @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.title = e.detail.value;
    }}
        ></x-input>
        <x-entity-picker
          placeholder=${this.t.translate("field", "wiki", "parent")}
          clearable
          .value=${this._entry.value?.parentID}
          .options=${{
      class: WikiEntry,
      selectionSet: { title: 1 },
      filter: new AdornisFilter({
        searchFields: ["title"],
        aspects: [
          new StringAspect({
            fieldName: "_class",
            mode: "equals",
            value: WikiEntry._class
          })
        ]
      })
    }}
          @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.parentID = e.detail.value ?? null;
    }}
        >
        </x-entity-picker>
        ${this._isDraft() ? html`
              <x-dropdown-selection
                placeholder=${this.t.translate("field", "wiki", "status")}
                .selectables=${Object.keys(WikiEntryDraftStatus)}
                .renderString=${(key) => WikiEntryDraftStatus[key]}
                .value=${this._entry.value?.status}
                @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.status = e.detail.value;
    }}
              ></x-dropdown-selection>
            ` : html`<x-checkbox
              label=${this.t.translate("field", "wiki", "published")}
              .value=${this._entry.value?.published}
              @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.published = e.detail.value;
    }}
            ></x-checkbox>`}
      </x-grid>
    `;
  }
  /**
   * Renders the save button with appropriate state and handlers
   * @returns {Renderable} Save button template
   */
  _saveButton() {
    return html`
      <x-button
        ${css({ minWidth: "initial" })}
        @click=${async () => {
      if (this._entry.value?.title?.trim().length === 0) {
        XSnackbar.show(this.t.translate("wiki", "actions", "titleRequired"));
        return;
      }
      this._loading.next(true);
      if (this._entry.value?._id) {
        await this._entry.value.save();
        this.dispatchEvent(
          new CustomEvent("entry-saved", {
            detail: {
              value: this._entry.value._id
            }
          })
        );
        XSnackbar.show(this.t.translate("wiki", "entrySaved"));
      } else if (this._entry.value) {
        this.entryID.next(await this._entry.value.create());
        XSnackbar.show(this.t.translate("wiki", "entryCreated"));
        this.dispatchEvent(
          new CustomEvent("entry-created", {
            detail: {
              value: this.entryID.value
            }
          })
        );
      }
      this._loading.next(false);
    }}
      >
        ${this._entry.value?._id ? this.t.translate("wiki", "actions", "save") : this.t.translate("wiki", "actions", "create")}
      </x-button>
    `;
  }
  /**
   * Renders the action buttons section (create draft, apply draft)
   * @returns {Renderable} Action buttons template
   */
  _actions() {
    let columns = "110px 60px";
    if (this.entryID.value) {
      columns = "180px " + columns;
    }
    if (this._isDraft()) {
      columns += " 60px";
    }
    return html`
      <x-grid
        columns="${columns}"
        space="sm"
        slot="actions"
        ${css({
      alignItems: "start"
    })}
      >
        ${this.entryID.value ? html`
              <x-button
                ${css({ minWidth: "initial" })}
                tone="neutral"
                @click=${async () => {
      if (this._isDraft()) {
        await this._applyDraft();
      } else {
        await this._createDraft();
      }
    }}
              >
                ${this._isDraft() ? this.t.translate("wiki", "actions", "applyDraft") : this.t.translate("wiki", "actions", "createDraft")}
              </x-button>
            ` : nothing}
        ${this._saveButton()}
        <x-button
          ${css({ minWidth: "initial" })}
          tone="error"
          @click=${async () => {
      if (this._entry.value?.isDirty && !await XDialog.confirm(this.t.translate("wiki", "actions", "confirmCancel"))) {
        return;
      }
      this._entry.value?.revert();
      this.dispatchEvent(
        new CustomEvent("entry-abort", {
          detail: {
            isDraft: this._isDraft(),
            referenceID: this._entry.value?.referenceID,
            value: this._entry.value?._id
          }
        })
      );
    }}
        >
          <x-tooltip> <x-text> ${this.t.translate("wiki", "actions", "cancel")} </x-text> </x-tooltip>
          <x-icon>close</x-icon>
        </x-button>
        ${this._isDraft() ? html`
              <x-button
                ${css({ minWidth: "initial" })}
                tone="error"
                @click=${async () => {
      if (this._entry.value?.isDirty && !await XDialog.confirm(this.t.translate("wiki", "actions", "confirmDraftDelete"))) {
        return;
      }
      const _id = this._entry.value?._id;
      const referenceID = this._entry.value?.referenceID;
      await this._entry.value?.remove();
      this.dispatchEvent(
        new CustomEvent("entry-deleted", {
          detail: {
            value: _id,
            referenceID
          }
        })
      );
    }}
              >
                <x-tooltip> <x-text> ${this.t.translate("wiki", "actions", "delete")} </x-text> </x-tooltip>
                <x-icon>delete</x-icon>
              </x-button>
            ` : nothing}
      </x-grid>
    `;
  }
  /**
   * Renders the drafts section showing related draft entries
   * @returns {Renderable} Drafts list template
   */
  _drafts() {
    if (this._isDraft() || this._isTemplate() || !this._entry.value?._id) {
      return nothing;
    }
    return html`
      <x-flex
        padding="sm"
        ${css({
      background: "white",
      border: "1px solid rgba(0,0,0,0.2)",
      borderRadius: this.sizes.borderRadiusSecondary
    })}
      >
        <x-wiki-draft-list
          .referenceID=${this.entryID.value}
          @entry-navigate=${(e) => {
      this.dispatchEvent(new CustomEvent("entry-navigate", { detail: e.detail }));
    }}
        ></x-wiki-draft-list>
      </x-flex>
    `;
  }
  /**
   * Renders the permission selection dropdown
   * @returns {Renderable} Permission selection template
   */
  _permissionSelection() {
    if ((this._permissions.value?.length ?? 0) === 0) {
      return nothing;
    }
    return html`<x-grid
      columns="200px 200px"
      space="sm"
      horizontal
      ${css({
      paddingTop: this.spacing.sm,
      borderTop: "1px solid rgba(0,0,0,0.1)"
    })}
    >
      <x-dropdown-search-selection
        placeholder=${this.t.translate("field", "wiki", "readPermission")}
        .search=${(search) => {
      return (this._permissions.value ?? []).filter(
        (perm) => perm.name?.toLowerCase().includes(search?.toLowerCase() ?? "")
      );
    }}
        .renderString=${(perm) => perm?.name ?? ""}
        .value=${this._permissions.value?.find((perm) => perm.id === this._entry.value?.readPermission)}
        @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.readPermission = e.detail.value.id;
    }}
      ></x-dropdown-search-selection>
      <x-dropdown-search-selection
        placeholder=${this.t.translate("field", "wiki", "writePermission")}
        .search=${(search) => {
      return (this._permissions.value ?? []).filter(
        (perm) => perm.name?.toLowerCase().includes(search?.toLowerCase() ?? "")
      );
    }}
        .renderString=${(perm) => perm?.name ?? ""}
        .value=${this._permissions.value?.find((perm) => perm.id === this._entry.value?.writePermission)}
        @value-picked=${(e) => {
      if (!this._entry.value) {
        return;
      }
      this._entry.value.writePermission = e.detail.value.id;
    }}
      ></x-dropdown-search-selection>
    </x-grid>`;
  }
  /**
   * Renders the action bar with save and other action buttons
   * @returns {Renderable} Action bar template
   */
  _actionBar() {
    const headerStyle = {
      border: "1px solid rgba(0,0,0,0.2)",
      background: "white",
      borderBottom: "none",
      borderRadius: `${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary} 0 0`
    };
    if (this._isTemplate()) {
      return html` <x-flex padding="sm" space="sm" end ${css({ ...headerStyle, alignItems: "end" })}>
        <x-flex ${css({ maxWidth: "120px", opacity: this._entry.isLoading ? 0 : 1 })}> ${this._saveButton()} </x-flex>
      </x-flex>`;
    }
    return html`
      <x-flex padding="sm" space="sm" ${css(headerStyle)}>
        <x-flex
          ?horizontal=${!this._isSmall()}
          ?crossaxis-center=${this._isSmall()}
          space-between
          space="sm"
          ${css({
      opacity: this._entry.isLoading ? 0 : 1,
      flexDirection: this._isSmall() ? "column-reverse" : void 0
    })}
        >
          ${this._titleParentEdit()} ${this._actions()}
        </x-flex>
        ${this._permissionSelection()}
      </x-flex>
    `;
  }
  /**
   * Renders the header section with title and metadata
   * @returns {Renderable} Header template
   */
  _header() {
    let title = html`<x-loader
      ${css({ width: "18px", height: "18px", margin: `${this.spacing.sm} 0` })}
    ></x-loader>`;
    if (!this._entry.value?._id && !this._entry.isLoading) {
      title = this.t.translate("wiki", "newEntry");
    } else if (this._entry.value?.title) {
      title = this._entry.value.title;
    }
    return xComponents(html`
      <x-flex space="sm">
        <x-flex
          slot="title"
          space-between
          center
          horizontal
          ${css({
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      paddingBottom: this.spacing.sm,
      alignItems: "end"
    })}
        >
          <x-h1>${title}</x-h1>
          ${this._isDraft() ? html`
                <x-flex
                  horizontal
                  space="sm"
                  crossaxis-center
                  ${css({
      backgroundColor: "rgba(0,0,0,0.1)",
      borderRadius: this.sizes.borderRadius,
      padding: `${this.spacing.xxs} ${this.spacing.sm}`
    })}
                >
                  <x-text>${this.t.translate("wiki", "draft")}</x-text>
                  <x-icon>edit_note</x-icon>
                </x-flex>
              ` : nothing}
        </x-flex>

        ${this._drafts()} ${this._actionBar()}
      </x-flex>
    `);
  }
  /**
   * Renders the footer section
   * @returns {Renderable} Footer template
   */
  _footer() {
    return html`
      ${this.debug ? html`<x-wiki-splitter-view .value=${this._entry.value?.content}></x-wiki-splitter-view>` : nothing}
    `;
  }
  /**
   * Renders the loading indicator
   * @returns {Renderable} Loader template
   */
  _loader() {
    if (!this._loading.value) {
      return nothing;
    }
    return html`<x-flex
      crossaxis-center
      ${css({
      zIndex: 100,
      position: "absolute",
      left: "0",
      top: "0",
      width: "100%",
      height: "100%",
      justifyContent: "center",
      backgroundColor: "rgba(255,255,255,0.8)"
    })}
    >
      <x-loader></x-loader>
    </x-flex>`;
  }
  _editorOrPreview() {
    if (this._preview.value) {
      return html`<x-flex padding="md">
        <x-flex
          ${css({
        marginBottom: this.spacing.md,
        paddingBottom: this.spacing.md,
        borderBottom: "1px solid rgba(0,0,0,0.1)"
      })}
        >
          <x-flex
            ${acss({
        cursor: "pointer",
        transition: "opacity 0.2s",
        "&:hover": {
          opacity: 0.5
        }
      })}
            horizontal
            space="sm"
            crossaxis-center
            @click=${() => this._preview.next(false)}
          >
            <x-icon>visibility_off</x-icon> <x-text>${this.t.translate("wiki", "leavePreview")}</x-text>
          </x-flex>
        </x-flex>
        <x-flex
          ${acss({
        [`${DesignSystem.prefix}-text:empty, x-text:empty`]: {
          height: "19px"
        }
      })}
        >
          ${xComponents(html`${unsafeHTML(this._entry.value?.content)}`)}
        </x-flex>
      </x-flex> `;
    }
    return html`
      <x-editor
        ${css({
      opacity: this._entry.isLoading ? 0 : 1,
      transition: "opacity 0.75s",
      paddingTop: this.spacing.lg
    })}
        .value=${this._entry.value?.content}
        .configs=${[
      ...fullEditorConfig(),
      ...this.additionalConfigs,
      embedding(
        (this._isDraft() ? this._entry.value?.referenceID : this._entry.value?._id) ?? ""
      ),
      preview(() => {
        this._preview.next(true);
      }),
      {
        plugins: (schema) => {
          return [
            obsidianLinkPlugin(async (search) => {
              const wikiEntries = await WikiEntry.getAll()({ _id: 1, title: 1 });
              const rootUrl = baseConfig.get("ROOT_URL");
              return wikiEntries.filter(
                (entry) => entry.title?.toLowerCase().includes(search.toLowerCase()) || search.toLowerCase().includes(entry._id?.toLowerCase())
              ).map(
                (entry) => ({ href: `${rootUrl}wiki/${entry._id}|${entry.title}`, name: entry.title })
              );
            })
          ];
        }
      }
    ]}
        @value-changed=${async (e) => {
      if (!e.detail?.value || !this._entry.value)
        return;
      this._entry.value.content = e.detail.value;
    }}
      ></x-editor>
    `;
  }
  /**
   * Renders the main editor content
   * @returns {Renderable} Editor template with all sections
   */
  render() {
    return xComponents(html`
      <x-flex ${css({ maxWidth: "1400px", margin: "0 auto", position: "relative", paddingBottom: this.spacing.xl })}>
        ${this._loader()}
        ${guard(
      [this._entry.value?.title, this._entry.value?._class, this.entryID.value, this._size.value],
      () => this._header()
    )}
        <x-flex
          ${css({
      background: "white",
      border: "1px solid rgba(0,0,0,0.2)"
    })}
        >
          ${this._editorOrPreview()}
        </x-flex>
        ${this._footer()}
      </x-flex>
    `);
  }
};
__decorateClass([
  property({ attribute: false })
], XWikiEditor.prototype, "additionalConfigs", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "_size", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "t", 2);
__decorateClass([
  property({ attribute: false })
], XWikiEditor.prototype, "entryID", 2);
__decorateClass([
  property({ type: Boolean })
], XWikiEditor.prototype, "debug", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "_entry", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "_loading", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "_preview", 2);
__decorateClass([
  state()
], XWikiEditor.prototype, "_permissions", 2);
XWikiEditor = __decorateClass([
  customElement("x-wiki-editor")
], XWikiEditor);
export {
  XWikiEditor
};
